import localforage from "localforage";

export interface AttendanceRecord {
  id: string;
  user: string; // user id
  first_name?: string;
  last_name?: string;
  department?: string;
  profile_pic?: string;
  type: "clock-in" | "clock-out";
  timestamp: string; // ISO string
  photoBase64: string;
  photoKey?: string; // If already uploaded to AWS but attendance failed
  signature?: number[]; // Added to pass offline signature to server
  ppe_compliant?: boolean; // Indicates if PPE was confirmed during offline clock-in
  is_tampered?: boolean; // Flag to indicate if time manipulation was detected while offline
}

let attendanceQueueStore: LocalForage | null = null;
const getAttendanceQueueStore = () => {
  if (!attendanceQueueStore) {
    attendanceQueueStore = localforage.createInstance({
      name: "smcrm_attendance",
      storeName: "attendance_queue",
    });
  }
  return attendanceQueueStore;
};

export async function enqueueRecord(record: Omit<AttendanceRecord, "id">): Promise<boolean> {
  try {
    const store = getAttendanceQueueStore();
    const id = `attendance_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const fullRecord: AttendanceRecord = { ...record, id };
    await store.setItem(id, fullRecord);
    
    console.log(`[OfflineQueue] Enqueued offline record for user ${record.user}. Type: ${record.type}, Time: ${record.timestamp}, Tampered: ${record.is_tampered}`);
    window.dispatchEvent(new CustomEvent("attendance-queue-updated"));
    return true;
  } catch (err) {
    console.error("Failed to enqueue attendance record:", err);
    return false;
  }
}

export async function getPendingRecords(): Promise<AttendanceRecord[]> {
  try {
    const store = getAttendanceQueueStore();
    const records: AttendanceRecord[] = [];
    await store.iterate<AttendanceRecord, void>((value) => {
      records.push(value);
    });
    // Sort oldest first to process in order
    return records.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  } catch (err) {
    console.error("Failed to get pending attendance records:", err);
    return [];
  }
}

export async function removeRecord(id: string): Promise<boolean> {
  try {
    const store = getAttendanceQueueStore();
    await store.removeItem(id);
    window.dispatchEvent(new CustomEvent("attendance-queue-updated"));
    return true;
  } catch (err) {
    console.error(`Failed to remove attendance record ${id}:`, err);
    return false;
  }
}

export async function updateRecord(id: string, updates: Partial<AttendanceRecord>): Promise<boolean> {
    try {
      const store = getAttendanceQueueStore();
      const existing = await store.getItem<AttendanceRecord>(id);
      if (existing) {
          await store.setItem(id, { ...existing, ...updates });
          return true;
      }
      return false;
    } catch (err) {
      console.error(`Failed to update attendance record ${id}:`, err);
      return false;
    }
}

export async function clearQueue(): Promise<boolean> {
  try {
    const store = getAttendanceQueueStore();
    await store.clear();
    window.dispatchEvent(new CustomEvent("attendance-queue-updated"));
    return true;
  } catch (err) {
    console.error("Failed to clear attendance queue:", err);
    return false;
  }
}
